import styles from './ccf-consumer-card.module.scss';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAppSpaceRatio } from '../../global.app.slice';

import {
  CcfConsumerCardOverviewTab,
  CcfConsumerCardHeader,
  CcfConsumerCardInteractionsTab,
  CcfConsumerCardDetailsTab,
  CcfConsumerCardNotesTab,
  CcfConsumerCardExternalDataTab,
  CcfConsumerCardTicketsTab,
} from '@nice-ccf/ui-components';
import { CcfTabs } from '@nice-ccf/ui-controls';
import {
  activeContactSelector,
  setNewCommitment,
  updateConsumerAppSpaceTab,
  updateConsumerAppSpaceTabIndex,
  selectConsumerAppSpaceActiveTab,
  selectConsumerAppSpaceActiveIndex,
} from '../../ccf-inbox/ccf-inbox.slice';
import { FormControl, MenuItem, Select } from '@mui/material';
import { useTranslator } from '@nice-ccf/ui-controls';
import { ConsumerAppSpace } from '../../../enums/consumer-app-space-tabs';
import { ConsumerCardTab } from './model/consumer-app-space-tab';

/* eslint-disable-next-line */
export interface CcfConsumerCardProps {}

/**
 * CcfAppSpace - returns app space panel
 * @param props - CcfAppSpaceProps
 * @returns - appspace
 * @example - `<CcfAppSpace />`
 */
export function CcfConsumerCard(props: CcfConsumerCardProps) {
  const activeContact = useSelector(activeContactSelector);

  const currentTab = useSelector(selectConsumerAppSpaceActiveIndex);
  const activeTab = useSelector(selectConsumerAppSpaceActiveTab);
  const [isMinViewVisible, setIsMinViewVisible] = useState(false);
  const dispatch = useDispatch();
  const [translate] = useTranslator();
  const appSpaceRatio = useSelector(getAppSpaceRatio);

  useEffect(() => {
    console.log('appSpaceRatio', appSpaceRatio);
    if (appSpaceRatio && appSpaceRatio <= 40) {
      setIsMinViewVisible(true);
    } else {
      setIsMinViewVisible(false);
    }
  }, [appSpaceRatio]);

  const appSpaceTabs: ConsumerCardTab[] = [
    {
      element: <CcfConsumerCardOverviewTab />,
      tooltip: translate('overview'),
      isActive: true,
      menuName: translate(ConsumerAppSpace.OVERVIEW),
    },
    {
      element: <CcfConsumerCardDetailsTab />,
      tooltip: translate('details'),
      isActive: true,
      menuName: translate(ConsumerAppSpace.DETAILS),
    },
    {
      element: <CcfConsumerCardInteractionsTab />,
      tooltip: translate('interactions'),
      isActive: true,
      menuName: translate(ConsumerAppSpace.INTERACTIONS),
    },

    {
      element: <CcfConsumerCardNotesTab />,
      tooltip: translate('notes'),
      isActive: true,
      menuName: translate(ConsumerAppSpace.NOTES),
    },
    {
      element: <CcfConsumerCardExternalDataTab />,
      tooltip: translate('externalData'),
      isActive: true,
      menuName: translate(ConsumerAppSpace.EXTERNALDATA),
    },
    ...(activeContact?.customer.consumerCardTicketTabHide
      ? [
          {
            element: <CcfConsumerCardTicketsTab />,
            tooltip: translate('tickets'),
            isActive: true,
            menuName: translate(ConsumerAppSpace.TICKETS),
          },
        ]
      : []),
  ];

  const [appSpaceMenus, setAppSpacemenus] = useState(appSpaceTabs);

  const [selectedTab, setSelectedTab] = useState('Overview');

  /**
   * Used to update the app space tab menuName and tab index
   * @param newTabIndex - number
   * @example - handleChange(0)
   */
  const handleSelectChange = (e: any) => {
    dispatch(setNewCommitment(false));
    setSelectedTab(e.target.value);
    dispatch(updateConsumerAppSpaceTab({ tab: e.target.value }));
  };

  /**
   * Used to update the app space tab menuName and tab index
   * @param newTabIndex - number
   * @example - handleChange(0)
   */
  const handleChange = (newTabIndex: number) => {
    dispatch(setNewCommitment(false));
    setSelectedTab(appSpaceMenus[newTabIndex].menuName);
    dispatch(
      updateConsumerAppSpaceTab({ tab: appSpaceMenus[newTabIndex].menuName })
    );
    setSelectedTab(appSpaceMenus[newTabIndex].menuName); //To update selected tab in min view dropdown
  };

  useEffect(() => {
    if (activeTab) {
      let activeTabIndex = 0;
      const activeTabs = appSpaceMenus.filter((tab) => tab.isActive);
      if (activeTabs.find((tab) => tab.menuName === activeTab)) {
        activeTabIndex = activeTabs.findIndex(
          (tab) => tab.menuName === activeTab
        );
      }

      dispatch(updateConsumerAppSpaceTabIndex({ index: activeTabIndex }));
    }
  }, [activeTab]);

  return (
    <section id="agent_consumerCard_Header" className={styles.appSpaceCard}>
      <section className={styles.appSpaceHeader}>
        <CcfConsumerCardHeader
          isMinViewVisible={isMinViewVisible}
          activeContact={activeContact}
        />
      </section>
      {isMinViewVisible && (
        <div
          style={{ borderBottom: '1px solid #dde4e9' }}
          id="agent_consumerCard_minView_dropdownDiv"
        >
          <FormControl
            variant="standard"
            sx={{
              margin: '10px 0px 0px 10px',
              borderBottom: '2px solid #005C99',
            }}
          >
            <Select
              variant="standard"
              value={selectedTab}
              onChange={(e: any) => {
                handleSelectChange(e);
              }}
              className={styles.customDropdown}
              id="agent_consumerCard_minView_dropdown"
            >
              {appSpaceTabs.map((key: any) => (
                <MenuItem
                  id={`agent_consumerCard_minView_dropdown_${key.menuName}`}
                  key={key.menuName}
                  value={key.menuName}
                >
                  {key.menuName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      )}
      <CcfTabs
        id="agent_consumerCard_tabs_container"
        currentTab={currentTab}
        variant={CcfTabs.Variant.FULL_WIDTH}
        setCurrentTab={handleChange}
        classes={{
          root: styles.tabsContainer,
          indicator: styles.activeTabIndicator,
        }}
        sx={isMinViewVisible ? { display: 'None' } : { display: 'flex' }}
      >
        {appSpaceMenus.map((tab, index) => (
          <CcfTabs.TabPanel
            classes={{
              root: styles.tab,
              selected: styles.tabSelected,
            }}
            tooltip={tab.tooltip}
            key={index}
            label={tab.menuName}
          >
            <section
              style={
                isMinViewVisible
                  ? {
                      overflowY: [2, 0, 3, 5].includes(currentTab)
                        ? 'hidden'
                        : 'auto',
                      height: 'calc( 100% - 140px)',
                    }
                  : {
                      overflowY: [2, 0, 3, 5].includes(currentTab)
                        ? 'hidden'
                        : 'auto',
                      height: 'calc(100% - 105px)',
                    }
              }
              className={styles.tabContentSection}
              id={`agent_consumerCard_${tab.element}_tab`}
            >
              {tab.element}
            </section>
          </CcfTabs.TabPanel>
        ))}
      </CcfTabs>
    </section>
  );
}

export default CcfConsumerCard;
